'use client'

import { useRouter } from 'next/router'
import { createClient } from '@/lib/supabase/component'
import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { URI_ACCOUNT, URI_SIGN_IN, URI_SIGN_UP } from '@/lib/utils/constants';
import { useReferenceValuesStore } from '@/lib/stores/layout';
import { useSaveStore } from '@/lib/stores/post';
import { fetchReferenceValues } from '@/lib/utils/helpers';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  // State
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  // Refs for avoiding closure issues and preventing duplicate calls
  const profileRef = useRef(null);
  const isLoadingUserData = useRef(false);

  // Hooks
  const router = useRouter();
  const supabase = createClient();
  const { setReferenceValues } = useReferenceValuesStore();
  const { getUserSavedStories } = useSaveStore();

  // Helper functions
  const clearUserData = () => {
    setReferenceValues({});
    useSaveStore.setState({ userSavedStoryIds: [] });
  };

  const loadUserData = async () => {
    try {
      if (isLoadingUserData.current) return;
      isLoadingUserData.current = true;

      // Fetch reference values if we don't have them yet
      const currentReferenceValues = useReferenceValuesStore.getState().referenceValues;
      if (Object.keys(currentReferenceValues).length === 0) {
        const categorizedData = await fetchReferenceValues(supabase);
        setReferenceValues(categorizedData);
      }

      // Load user's saved stories
      const currentUserSavedStories = useSaveStore.getState().userSavedStoryIds;
      if (currentUserSavedStories.length === 0) {
        getUserSavedStories(supabase);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      isLoadingUserData.current = false;
    }
  };

  // Sign out function
  const signOut = async () => {
    await supabase.auth.signOut();
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    if (!user) return { error: { message: 'Not authenticated' } };

    const { full_name, avatar_url } = updates;
    const updatedRawUserMeta = {
      ...(full_name ? { full_name } : {}),
      ...(avatar_url ? { avatar_url } : {}),
    };

    try {
      // Check if profile exists
      if (profile) {
        // Update existing profile
        const { data, error } = await supabase
          .from('profiles')
          .update({
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .eq('id', user.id);

        if (error) throw error;

        // Update main raw user metadata (includes avatar_url for getUserAvatar function)
        await supabase.auth.updateUser({
          data: {
            ...updatedRawUserMeta,
          },
        });

        // Update local state
        setProfile({ ...profile, ...updates });

        return { data };
      } else {
        // Create new profile
        const { data, error } = await supabase
          .from('profiles')
          .insert([
            {
              id: user.id,
              ...updates,
            }
          ]);

        if (error) throw error;

        // Update main raw user metadata for new profile
        await supabase.auth.updateUser({
          data: {
            ...updatedRawUserMeta,
          },
        });

        // Update local state
        setProfile({ id: user.id, ...updates });

        return { data };
      }
    } catch (error) {
      return { error };
    }
  };

  useEffect(() => {
    // Get current session and set up auth state listener
    const setupAuth = async () => {
      setLoading(true);

      // Get initial session
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);

      if (session?.user) {
        // Fetch reference values for authenticated users only (non-blocking)
        loadUserData();

        // Fetch user profile if logged in
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        setProfile(data || null);
        profileRef.current = data || null;
      }

      // Set initial body class based on auth state
      if (typeof window !== 'undefined') {
        if (session?.user) {
          document.body.classList.add('mom-user');
        } else {
          document.body.classList.remove('mom-user');
        }
      }

      // Set up auth change listener
      /** @see https://github.com/orgs/supabase/discussions/19011 */
      /** @see https://supabase.com/docs/reference/javascript/auth-onauthstatechange */
      /*
       Do not use other Supabase functions in the callback function.
       If you must, dispatch the functions once the callback has finished executing.
       */
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          setUser(session?.user || null);

          if (session?.user) {
            setTimeout(async () => {
              loadUserData(); // Non-blocking

              // Fetch user profile only if we don't have it yet
              if (!profileRef.current) {
                const { data } = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', session.user.id)
                  .single();

                setProfile(data || null);
                profileRef.current = data || null;
              }
            }, 0)
          } else {
            setProfile(null);
            profileRef.current = null;

            // Clear reference values and saved stories when user signs out
            clearUserData();
          }

          // Update body class based on auth state
          if (typeof window !== 'undefined') {
            if (session?.user) {
              document.body.classList.add('mom-user');
            } else {
              document.body.classList.remove('mom-user');
            }
          }

          setLoading(false);

          const currentPath = window.location.pathname;
          switch (event) {
            case 'SIGNED_IN':
              if (currentPath === URI_SIGN_IN || currentPath === URI_SIGN_UP) {
                router.push(URI_ACCOUNT)
              }
              break;

            case 'SIGNED_OUT':
              if (currentPath.includes(URI_ACCOUNT)) {
                router.push(URI_SIGN_IN)
              }
              break;
          }
        }
      );

      setLoading(false);

      // Clean up subscription on unmount
      return () => {
        subscription.unsubscribe();
      };
    };

    setupAuth();
  }, [])

  const value = {
    user,
    profile,
    loading,
    signOut,
    updateUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
